import { createAction, handleActions } from 'redux-actions';
import {
  API_SUCCESSION_CRITICALITY,
  API_SUCCESSION_JOB_FAMILIES,
  API_SUCCESSION_READINESS,
  API_SUCCESSION_REMOVAL_REASONS,
  API_SUCCESSION_SETTINGS,
  api_succession_catalog_item,
  api_succession_catalog_ordering,
} from 'constants/apiRoutes';
import {
  REMOVAL_REASON_USED_BY,
  SUCCESSION_SETTINGS_FIELD_NAMES,
} from 'constants/succession';
import http from 'utility/http';

// ------------------------------------
// Constants
// ------------------------------------

const SET_SUCCESSION_SETTINGS = 'SET_SUCCESSION_SETTINGS';
const SET_SUCCESSION_READINESS = 'SET_SUCCESSION_READINESS';
const SET_SUCCESSION_CRITICALITY = 'SET_SUCCESSION_CRITICALITY';
const SET_SUCCESSION_REMOVAL_REASONS = 'SET_SUCCESSION_REMOVAL_REASONS';
const SET_SUCCESSION_JOB_FAMILIES = 'SET_SUCCESSION_JOB_FAMILIES';
const SET_SUCCESSION_COVERAGE = 'SET_SUCCESSION_COVERAGE';

const {
  READINESS,
  CRITICALITY,
  REMOVAL_REASONS,
  JOB_FAMILIES,
  GENERIC_COVERAGE_RATIO,
} = SUCCESSION_SETTINGS_FIELD_NAMES;

const SETTINGS_BASE_URLS = {
  [READINESS]: API_SUCCESSION_READINESS,
  [CRITICALITY]: API_SUCCESSION_CRITICALITY,
  [REMOVAL_REASONS]: API_SUCCESSION_REMOVAL_REASONS,
  [JOB_FAMILIES]: API_SUCCESSION_JOB_FAMILIES,
  [GENERIC_COVERAGE_RATIO]: API_SUCCESSION_SETTINGS,
};

const mapRemovalReasons = reasons =>
  reasons.map(reason => ({
    ...reason,
    used_by: reason.is_system
      ? REMOVAL_REASON_USED_BY.SYSTEM
      : REMOVAL_REASON_USED_BY.USERS,
  }));

const initialState = {
  [READINESS]: [],
  [CRITICALITY]: [],
  [REMOVAL_REASONS]: [],
  [JOB_FAMILIES]: [],
  [GENERIC_COVERAGE_RATIO]: 0,
};

// ------------------------------------
// Actions
// ------------------------------------

export const setSuccessionSettings = createAction(SET_SUCCESSION_SETTINGS);
export const setSuccessionReadiness = createAction(SET_SUCCESSION_READINESS);
export const setSuccessionCriticality = createAction(
  SET_SUCCESSION_CRITICALITY
);
export const setSuccessionRemovalReasons = createAction(
  SET_SUCCESSION_REMOVAL_REASONS
);
export const setSuccessionJobFamilies = createAction(
  SET_SUCCESSION_JOB_FAMILIES
);
export const setSuccessionCoverage = createAction(SET_SUCCESSION_COVERAGE);

// ------------------------------------
// Reducers
// ------------------------------------

const reducers = {
  [SET_SUCCESSION_SETTINGS]: (state, { payload }) => payload,
  [SET_SUCCESSION_READINESS]: (state, { payload }) => ({
    ...state,
    [READINESS]: payload,
  }),
  [SET_SUCCESSION_CRITICALITY]: (state, { payload }) => ({
    ...state,
    [CRITICALITY]: payload,
  }),
  [SET_SUCCESSION_REMOVAL_REASONS]: (state, { payload }) => ({
    ...state,
    [REMOVAL_REASONS]: payload,
  }),
  [SET_SUCCESSION_JOB_FAMILIES]: (state, { payload }) => ({
    ...state,
    [JOB_FAMILIES]: payload,
  }),
  [SET_SUCCESSION_COVERAGE]: (state, { payload }) => ({
    ...state,
    [GENERIC_COVERAGE_RATIO]: payload,
  }),
};

// ------------------------------------
// API calls
// ------------------------------------

export const getSuccessionCoverage = dispatch =>
  http.get(API_SUCCESSION_SETTINGS).then(({ data }) => {
    const ratio = data.generic_coverage_ratio;
    dispatch(setSuccessionCoverage(ratio));
    return ratio;
  });

export const getSuccessionReadiness = dispatch =>
  http.get(API_SUCCESSION_READINESS).then(({ data }) => {
    dispatch(setSuccessionReadiness(data));
    return data;
  });

export const getSuccessionCriticality = dispatch =>
  http.get(API_SUCCESSION_CRITICALITY).then(({ data }) => {
    dispatch(setSuccessionCriticality(data));
    return data;
  });

export const getSuccessionRemovalReasons = dispatch =>
  http.get(API_SUCCESSION_REMOVAL_REASONS).then(({ data }) => {
    const removalReasons = mapRemovalReasons(data);
    dispatch(setSuccessionRemovalReasons(removalReasons));
    return removalReasons;
  });

export const getSuccessionJobFamilies = dispatch =>
  http.get(API_SUCCESSION_JOB_FAMILIES).then(({ data }) => {
    dispatch(setSuccessionJobFamilies(data));
    return data;
  });

export const getAllSuccessionSettings = dispatch =>
  Promise.all([
    http.get(API_SUCCESSION_SETTINGS),
    http.get(API_SUCCESSION_READINESS),
    http.get(API_SUCCESSION_CRITICALITY),
    http.get(API_SUCCESSION_REMOVAL_REASONS),
    http.get(API_SUCCESSION_JOB_FAMILIES),
  ]).then(results => {
    const [
      coverage,
      readinessLevels,
      criticalityLevels,
      removalReasons,
      jobFamilies,
    ] = results.map(result => result.data);

    const settings = {
      [READINESS]: readinessLevels,
      [CRITICALITY]: criticalityLevels,
      [REMOVAL_REASONS]: mapRemovalReasons(removalReasons),
      [JOB_FAMILIES]: jobFamilies,
      [GENERIC_COVERAGE_RATIO]: coverage.generic_coverage_ratio,
    };
    dispatch(setSuccessionSettings(settings));
    return settings;
  });

const SETTINGS_FETCHERS = {
  [READINESS]: getSuccessionReadiness,
  [CRITICALITY]: getSuccessionCriticality,
  [REMOVAL_REASONS]: getSuccessionRemovalReasons,
  [JOB_FAMILIES]: getSuccessionJobFamilies,
  [GENERIC_COVERAGE_RATIO]: getSuccessionCoverage,
};

export const updateSuccessionCoverageRatio = (dispatch, ratio) =>
  http
    .patch(API_SUCCESSION_SETTINGS, {
      [GENERIC_COVERAGE_RATIO]: ratio,
    })
    .then(() => SETTINGS_FETCHERS[GENERIC_COVERAGE_RATIO](dispatch));

export const saveSuccessionSetting = (dispatch, key, item) => {
  const apiUrl = SETTINGS_BASE_URLS[key];
  const body =
    key === JOB_FAMILIES
      ? { name: item.name, is_active: item.is_active }
      : { label: item.label, is_active: item.is_active };

  const request = item.id
    ? http.patch(api_succession_catalog_item(apiUrl, item.id), body)
    : http.post(apiUrl, body);

  return request.then(() => SETTINGS_FETCHERS[key](dispatch));
};

export const reorderSuccessionCatalog = (dispatch, key, orderedIds) =>
  http
    .put(api_succession_catalog_ordering(SETTINGS_BASE_URLS[key]), {
      order: orderedIds,
    })
    .then(() => SETTINGS_FETCHERS[key](dispatch));

export default handleActions(reducers, initialState);

// ------------------------------------
// "plain" action functions
// ------------------------------------

export const clearSuccessionSettings = () =>
  setSuccessionSettings(initialState);
